import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { breakpoints } from '@/layouts/breakpoints'
import {
  Zap,
  Book,
  Terminal,
  FileCode,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';


import styled  from 'styled-components';

const StyledComp = styled.div`
    padding: 120px 40px;

    @media (max-width: ${breakpoints.medium}) {
      padding: 40px;
    }
    .docs-content {
      max-width: 1400px;
      margin: 0 auto;
      display: flex;
      gap: clamp(30px, 5vw, 60px);
      align-items: center;

      @media (max-width: 968px) {
        flex-direction: column;
        align-items: stretch;
      }

      .left {
        flex: 1;
        min-width: 0;

        h2 {
          font-size: 36px;
          margin-bottom: 24px;
        }

        p {
          line-height: 1.8;
          color: var(--grey);
          margin-bottom: 32px;
        }

        .docs-features {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 32px;

          .feature {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: clamp(14px, 1.5vw, 15px);

            svg {
              flex-shrink: 0;
            }
          }
        }

        .docs-actions {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;

          a {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 0 24px;
            border-radius: 8px;
            font-size: 14px;
            text-decoration: none;
            cursor: pointer;
            transition: all 0.3s;
            min-height: 44px;

            &.primary {
              background-color: var(--black);
              color: var(--white);
              border: 1px solid var(--black);

              &:hover {
                background-color: #333;
              }
            }

            &.secondary {
              background-color: transparent;
              color: var(--black);
              &:hover {
                background-color: #eee;
              }
            }
          }
        }
      }

      .right {
        width: 100%;
        max-width: 500px;
        flex-shrink: 0;

        @media (max-width: 968px) {
          max-width: 100%;
        }

        .docs-preview {
          background-color: rgba(0, 0, 0, 0.02);
          border: 1px solid rgba(var(--greyr), 0.3);
          border-radius: 8px;
          padding: clamp(24px, 4vw, 32px);

          .preview-header {
            display: flex;
            gap: 8px;
            margin-bottom: 24px;

            .dot {
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background-color: rgba(var(--greyr), 0.3);
            }
          }

          .preview-nav {
            display: flex;
            flex-direction: column;
            gap: 12px;

            .nav-item {
              display: flex;
              align-items: center;
              gap: 12px;
              padding: 12px;
              background-color: var(--white);
              border-radius: 6px;
              font-size: 14px;
              cursor: pointer;
              transition: all 0.2s;

              &:hover {
                background-color: rgba(0, 0, 0, 0.05);
              }

              &.active {
                background-color: var(--black);
                color: var(--white);

                svg {
                  color: var(--white);
                }
              }

              svg {
                width: 18px;
                height: 18px;
                color: var(--grey);
                flex-shrink: 0;
              }

              span {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }
        }
      }
    }

`;

const Comp = ({}) => {

  const navigate = useNavigate()

  return (
    <StyledComp>
      <div className="docs-content">
        <div className="left">
          <h2 className="serif">Python Financial Data API Documentation</h2>
          <p>
            Documentation for the market data SDK covers everything from quick start guides to advanced use cases.
            Interactive examples, code snippets, and detailed API references help you integrate
            faster and troubleshoot with confidence.
          </p>

          <div className="docs-features">
            <div className="feature">
              <CheckCircle size={20} color="var(--green)" />
              <span>Interactive API explorer with runnable examples</span>
            </div>
            <div className="feature">
              <CheckCircle size={20} color="var(--green)" />
              <span>Step-by-step integration guides for Python, TypeScript, and more</span>
            </div>
            <div className="feature">
              <CheckCircle size={20} color="var(--green)" />
              <span>Complete endpoint reference with parameters</span>
            </div>
            <div className="feature">
              <CheckCircle size={20} color="var(--green)" />
              <span>Code samples for trading strategies and backtesting</span>
            </div>
          </div>

          <div className="docs-actions">
            <Link to="/docs" className="primary">
              Browse docs <ArrowRight size={16} />
            </Link>
            <Link to="/dashboard/playground" className="secondary">API reference</Link>
          </div>
        </div>

        <div className="right">
          <div className="docs-preview">
            <div className="preview-header">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
            <div className="preview-nav">
              <div className="nav-item active">
                <Zap size={18} />
                <span>Quick Start</span>
              </div>
              <div className="nav-item" onClick={() => {navigate('/blog/')}}>
                <Book size={18} />
                <span>Guides & Examples</span>
              </div>
              <div className="nav-item" onClick={() => {navigate('/docs/')}}>
                <Terminal size={18} />
                <span>SDK Documentation</span>
              </div>
              <div className="nav-item" onClick={() => {navigate('/dashboard/playground')}}>
                <FileCode size={18} />
                <span>Code Playground</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StyledComp>
  );
};

export default Comp;
